import axios from 'axios';
import { useEffect, useReducer } from 'react';

const initialState = {
    loading: true,
    error: null,
    operatingCashFlows: [],
    initialFreeCashFlow: 0,
    outstandingShares: 0,
};

function cashFlowProjectionsReducer(state, action) {
    switch (action.type) {
        case 'load_start':
            return { ...state, loading: true, error: null };
        case 'load_success':
            return {
                ...state,
                loading: false,
                operatingCashFlows: action.payload.operatingCashFlows,
                initialFreeCashFlow: action.payload.initialFreeCashFlow,
                outstandingShares: action.payload.outstandingShares,
            };
        case 'load_error':
            return { ...state, loading: false, error: action.payload };
        default:
            return state;
    }
}

async function fetchCashFlowProjectionsData(apiUrl, tickerSymbol) {
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 19 }, (_, i) => currentYear - i - 1);

    const data = await Promise.all(
        years.map(async (year) => {
            const [cashFlowResponse, capexResponse] = await Promise.all([
                axios.get(`${apiUrl}/api/sec_api?ticker=${tickerSymbol}&year=${year}&category=cash_flows_from_operations`),
                axios.get(`${apiUrl}/api/sec_api?ticker=${tickerSymbol}&year=${year}&category=capital_expenditures`),
            ]);

            const operatingCashFlowsValue = cashFlowResponse.data.cash_flows_from_operations;
            const capitalExpendituresValue = capexResponse.data.capital_expenditures;
            const freeCashFlow = (typeof operatingCashFlowsValue === 'number' && typeof capitalExpendituresValue === 'number')
                ? operatingCashFlowsValue - capitalExpendituresValue
                : null;

            return {
                year,
                operatingCashFlows: operatingCashFlowsValue,
                capitalExpenditures: capitalExpendituresValue,
                freeCashFlow,
            };
        })
    );

    const freeCashFlowData = data
        .map((entry) => entry.freeCashFlow)
        .filter((value) => value !== null);

    let initialFreeCashFlow = 0;
    if (data.length > 0 && data[0].freeCashFlow !== null) {
        initialFreeCashFlow = data[0].freeCashFlow;
    } else if (freeCashFlowData.length > 0) {
        initialFreeCashFlow = freeCashFlowData[0];
    }

    const shares = await axios.get(`${apiUrl}/api/sec_api?ticker=${tickerSymbol}&year=${currentYear - 1}&category=shares_outstanding`);

    return {
        operatingCashFlows: data,
        initialFreeCashFlow,
        outstandingShares: shares.data.shares_outstanding ?? 0,
    };
}

export function useCashFlowProjectionsData(tickerSymbol) {
    const [state, dispatch] = useReducer(cashFlowProjectionsReducer, initialState);
    const apiUrl = process.env.REACT_APP_API_URL;

    useEffect(() => {
        if (!tickerSymbol) {
            return;
        }

        let cancelled = false;

        dispatch({ type: 'load_start' });
        fetchCashFlowProjectionsData(apiUrl, tickerSymbol)
            .then((payload) => {
                if (cancelled) {
                    return;
                }

                dispatch({
                    type: 'load_success',
                    payload,
                });
            })
            .catch((err) => {
                if (cancelled) {
                    return;
                }
                console.log(err);
                dispatch({ type: 'load_error', payload: err.message });
            });

        return () => {
            cancelled = true;
        };
    }, [apiUrl, tickerSymbol]);

    return state;
}
